import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Plus, Download, Loader2, Calendar, LayoutGrid } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useInstances } from '../../contexts/InstancesContext';
import { instagramPostsAPI, fluxosAPI } from '../../services/api';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import { toast } from 'react-toastify';
import { useConfirm } from '../../hooks/useConfirm';
import { POST_TYPES } from './constants';
import KanbanView from './KanbanView';
import CalendarView from './CalendarView';
import { CreateModal, DetailsModal } from './PostModals';

const InstagramPosts = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const { instances } = useInstances();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [fluxos, setFluxos] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('ig_viewMode') || 'kanban');
  const confirm = useConfirm();
  const fileRef = useRef();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightComment, setHighlightComment] = useState(null);

  const igInstances = instances.filter(i => (i.platform === 'instagram' || i.platform === 'facebook') && i.status === 'conectada');
  const [form, setForm] = useState({ tipo: 'image', caption: '', scheduled_at: '', files: [], previews: [], instancia_ids: [], comment_trigger_active: false, comment_trigger_keyword: '', comment_trigger_dm: '', comment_trigger_fluxo_id: '' });

  useEffect(() => { loadPosts(); loadFluxos(); }, []);

  // Abrir post via notificação (query params)
  useEffect(() => {
    const postMedia = searchParams.get('post_media');
    const commentId = searchParams.get('comment');
    if (!postMedia || loading) return;

    const post = posts.find(p => p.ig_media_id === postMedia || p.fb_post_id === postMedia || (postMedia.includes('_') && p.fb_post_id === postMedia.split('_').pop()) || (p.fb_post_id && p.fb_post_id.includes('_') && p.fb_post_id.split('_').pop() === postMedia));
    if (post) {
      setHighlightComment(commentId || null);
      openDetails(post);
      setSearchParams({}, { replace: true });
    } else if (posts.length > 0) {
      // Post não existe, tentar importar
      const platform = postMedia.includes('_') ? 'facebook' : 'instagram';
      const pageId = platform === 'facebook' ? postMedia.split('_')[0] : null;
      const inst = igInstances.find(i => platform === 'facebook' ? i.page_id === pageId : i.platform === 'instagram');
      if (inst) {
        instagramPostsAPI.importSingle(postMedia, platform, inst.page_id || inst.instagram_account_id).then(res => {
          if (res.data?.id) {
            loadPosts();
            setHighlightComment(commentId || null);
          }
        }).catch(() => {});
      }
      setSearchParams({}, { replace: true });
    }
  }, [posts, searchParams, loading]);

  // Prefill from Gerador de Ideias
  useEffect(() => {
    if (location.state?.prefill) {
      const { caption, tipo, horario } = location.state.prefill;
      let scheduled_at = '';
      if (horario) {
        const [h] = horario.replace('h', ':00').split(':');
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(parseInt(h) || 12, 0, 0, 0);
        scheduled_at = d.toISOString().slice(0, 16);
      }
      setForm(prev => ({ ...prev, caption: caption || '', tipo: tipo || 'image', scheduled_at }));
      setShowModal(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const res = await instagramPostsAPI.getAll(user.cliente_id);
      setPosts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadFluxos = async () => {
    try {
      const res = await fluxosAPI.list(user.cliente_id);
      setFluxos(res.data?.data || res.data || []);
    } catch (e) { console.error(e); }
  };

  const resetForm = () => {
    setForm({ tipo: 'image', caption: '', scheduled_at: '', files: [], previews: [], instancia_ids: [], comment_trigger_active: false, comment_trigger_keyword: '', comment_trigger_dm: '', comment_trigger_fluxo_id: '' });
    setEditingPost(null);
  };

  const openNew = (scheduledDate) => {
    resetForm();
    if (scheduledDate) {
      setForm(prev => ({ ...prev, scheduled_at: scheduledDate }));
    }
    setShowModal(true);
  };
  const openEdit = (post) => {
    setEditingPost(post);
    setForm({ tipo: post.tipo, caption: post.caption || '', scheduled_at: post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : '', files: [], previews: post.media_urls || [], instancia_ids: post.instancia_id ? [post.instancia_id] : [], comment_trigger_active: post.comment_trigger_active || false, comment_trigger_keyword: post.comment_trigger_keyword || '', comment_trigger_dm: post.comment_trigger_dm || '', comment_trigger_fluxo_id: post.comment_trigger_fluxo_id || '' });
    setShowModal(true);
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    const typeConfig = POST_TYPES.find(t => t.value === form.tipo);
    const limited = files.slice(0, typeConfig.maxFiles);
    const previews = limited.map(f => URL.createObjectURL(f));
    setForm(prev => ({ ...prev, files: limited, previews }));
  };

  // Converte datetime-local (sem timezone) para ISO com timezone local
  const toISOLocal = (dt) => dt ? new Date(dt).toISOString() : null;

  const handleSubmit = async (asDraft = false) => {
    try {
      setUploading(true);
      let mediaUrls = editingPost?.media_urls || [];
      if (form.files.length) {
        const fd = new FormData();
        form.files.forEach(f => fd.append('files', f));
        const uploadRes = await instagramPostsAPI.upload(fd);
        mediaUrls = uploadRes.data.keys;
      }
      if (!mediaUrls.length) return toast.warning('Selecione pelo menos uma mídia');
      if (!asDraft && !form.instancia_ids.length) return toast.warning('Selecione pelo menos uma conta para publicar/agendar');

      const instanciaIds = asDraft && !form.instancia_ids.length ? [''] : form.instancia_ids;

      const triggerFields = form.comment_trigger_active ? {
        comment_trigger_active: true,
        comment_trigger_keyword: form.comment_trigger_keyword,
        comment_trigger_dm: form.comment_trigger_dm,
        comment_trigger_fluxo_id: form.comment_trigger_fluxo_id || null,
      } : { comment_trigger_active: false };

      if (editingPost) {
        const inst = igInstances.find(i => i.id === instanciaIds[0]);
        await instagramPostsAPI.update(editingPost.id, {
          instancia_id: instanciaIds[0] || '',
          cliente_id: user.cliente_id,
          tipo: form.tipo,
          caption: form.caption,
          media_urls: mediaUrls,
          scheduled_at: asDraft ? null : toISOLocal(form.scheduled_at),
          status: asDraft ? 'draft' : (form.scheduled_at ? 'scheduled' : undefined),
          platform: inst?.platform === 'facebook' ? 'facebook' : 'instagram',
          ...triggerFields,
        });
      } else {
        for (const instId of instanciaIds) {
          const inst = igInstances.find(i => i.id === instId);
          await instagramPostsAPI.create({
            instancia_id: instId || '',
            cliente_id: user.cliente_id,
            tipo: form.tipo,
            caption: form.caption,
            media_urls: mediaUrls,
            scheduled_at: asDraft ? null : toISOLocal(form.scheduled_at),
            status: asDraft ? 'draft' : (form.scheduled_at ? 'scheduled' : undefined),
            platform: inst?.platform === 'facebook' ? 'facebook' : 'instagram',
            ...triggerFields,
          });
        }
        if (instanciaIds.length > 1) toast.success(`Postagem criada em ${instanciaIds.length} contas!`);
      }

      setShowModal(false);
      resetForm();
      loadPosts();
    } catch (e) {
      toast.error('Erro ao salvar: ' + (e.response?.data?.message || e.message));
    } finally { setUploading(false); }
  };

  const openDetails = async (post) => {
    setShowDetails(post);
    setDetailsData(null);
    setDetailsLoading(true);
    try {
      const res = await instagramPostsAPI.getDetails(post.id);
      setDetailsData(res.data);
    } catch (e) { console.error(e); }
    finally { setDetailsLoading(false); }
  };

  const handleImport = async () => {
    if (!igInstances.length) return;
    setImporting(true);
    try {
      let total = 0;
      for (const inst of igInstances) {
        try {
          const res = await instagramPostsAPI.importPosts(inst.id, user.cliente_id);
          total += res.data.imported || 0;
        } catch {}
      }
      toast.success(`${total} post(s) importado(s)!`);
      loadPosts();
    } catch (e) {
      toast.error('Erro ao importar: ' + (e.response?.data?.message || e.message));
    } finally { setImporting(false); }
  };

  const handleDelete = async (id) => {
    confirm({ title: 'Excluir postagem', message: 'Tem certeza que deseja excluir esta postagem?', onConfirm: async () => {
      await instagramPostsAPI.remove(id);
      loadPosts();
    }});
  };

  const handlePublishNow = async (id) => {
    confirm({ title: 'Publicar agora', message: 'Deseja publicar esta postagem agora?', type: 'info', confirmText: 'Publicar', onConfirm: async () => {
      const res = await instagramPostsAPI.publishNow(id);
      if (res.data.error) toast.error('Erro: ' + res.data.error);
      else toast.success('Publicado com sucesso!');
      loadPosts();
    }});
  };

  const pageBg = darkMode ? 'bg-zinc-900' : 'bg-gray-50';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';

  const filtered = filterType ? posts.filter(p => p.tipo === filterType) : posts;
  const drafts = filtered.filter(p => p.status === 'draft' || p.status === 'failed');
  const scheduled = filtered.filter(p => p.status === 'scheduled' || p.status === 'publishing');
  const published = filtered.filter(p => p.status === 'published');

  const getInstanceName = (id) => {
    const inst = igInstances.find(i => i.id === id);
    if (!inst) return '';
    if (inst.platform === 'facebook') return inst.nome_instancia;
    return `@${inst.instagram_username || inst.nome_instancia}`;
  };

  return (
    <Layout>
    <div className="space-y-6">
      <PageHeader title="Postagens" subtitle="Agende e publique conteúdo no Instagram e Facebook">
          <div className={`flex rounded-lg border ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
            <button onClick={() => { setViewMode('kanban'); localStorage.setItem('ig_viewMode', 'kanban'); }}
              className={`p-2 rounded-l-lg transition ${viewMode === 'kanban' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' : darkMode ? 'text-gray-400 hover:bg-zinc-700' : 'text-gray-500 hover:bg-gray-100'}`}
              title="Kanban">
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button onClick={() => { setViewMode('calendar'); localStorage.setItem('ig_viewMode', 'calendar'); }}
              className={`p-2 rounded-r-lg transition ${viewMode === 'calendar' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' : darkMode ? 'text-gray-400 hover:bg-zinc-700' : 'text-gray-500 hover:bg-gray-100'}`}
              title="Calendário">
              <Calendar className="w-4 h-4" />
            </button>
          </div>
          <button onClick={handleImport} disabled={importing}
            className={`flex items-center gap-1 sm:gap-2 px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg text-sm font-medium transition ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} <span className="hidden sm:inline">Importar Posts</span>
          </button>
          <button onClick={() => openNew()} className="flex items-center gap-1 sm:gap-2 px-2 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nova Postagem</span>
          </button>
      </PageHeader>

    <div className={`${pageBg} p-6 rounded-xl`}>

      {/* Filtros */}
      <div className="flex items-center gap-2 mb-4">
        {[{ value: '', label: 'Todos' }, ...POST_TYPES].map(t => {
          const Icon = t.icon;
          const count = t.value ? posts.filter(p => p.tipo === t.value).length : posts.length;
          return (
            <button key={t.value} onClick={() => setFilterType(t.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition ${
                filterType === t.value
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                  : darkMode ? 'bg-zinc-800 text-gray-400 hover:bg-zinc-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {t.label} <span className="opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* View */}
      {viewMode === 'kanban' ? (
        <KanbanView darkMode={darkMode} loading={loading} drafts={drafts} scheduled={scheduled} published={published}
          onEdit={openEdit} onDelete={handleDelete} onPublish={handlePublishNow} onView={openDetails} getInstanceName={getInstanceName} />
      ) : (
        <CalendarView darkMode={darkMode} posts={filtered} onView={openDetails} getInstanceName={getInstanceName}
          onNewPost={openNew} onDelete={handleDelete} onEdit={openEdit} />
      )}

      {/* Modals */}
      {showModal && (
        <CreateModal darkMode={darkMode} form={form} setForm={setForm} editingPost={editingPost}
          igInstances={igInstances} uploading={uploading} onSubmit={handleSubmit}
          onClose={() => { setShowModal(false); resetForm(); }} fileRef={fileRef} handleFiles={handleFiles} fluxos={fluxos} />
      )}
      {showDetails && (
        <DetailsModal darkMode={darkMode} post={showDetails} detailsData={detailsData}
          detailsLoading={detailsLoading} getInstanceName={getInstanceName} onClose={() => { setShowDetails(null); setHighlightComment(null); }}
          highlightComment={highlightComment} />
      )}
    </div>
    </div>
    </Layout>
  );
};

export default InstagramPosts;
